import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AccountActionDialog, type AccountTarget } from "./AccountActionDialog";
import { createSubmitLock } from "./action-model";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ApiError, resetPlayerPassword } from "@/lib/api";

export function ResetPasswordDialog({ target, onClose }: { target: AccountTarget; onClose: () => void }) {
  const submitting = useRef(createSubmitLock());
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) => resetPlayerPassword(id),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ["player-bans", id] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
    },
    onSettled: () => { submitting.current.end(); },
  });

  return <AccountActionDialog title="Reset password" scope="Resets the selected account's backend email/password login." target={target}
    pending={mutation.isPending} changed={false} onClose={onClose}>
    <p className="text-sm">Set this player's password to <code className="font-mono">password</code>. Existing sessions will be signed out and password lockouts cleared.</p>
    <Button onClick={() => {
      if (!submitting.current.begin()) return;
      mutation.mutate(target.id);
    }} disabled={mutation.isPending || mutation.isSuccess}>
      {mutation.isPending ? "Resetting…" : "Set password to password"}
    </Button>
    {mutation.error && <Alert variant="destructive"><AlertDescription>
      {mutation.error instanceof ApiError && mutation.error.message === "no_password_identity"
        ? "This account has no backend email/password identity."
        : String(mutation.error)}
    </AlertDescription></Alert>}
    {mutation.isSuccess && <Alert><AlertDescription>Password reset to <code>password</code>. Existing sessions signed out.</AlertDescription></Alert>}
  </AccountActionDialog>;
}
